import { useState, useEffect, useCallback, useMemo } from "react";
import { ProductUI, CartItem } from "../types";
import { getLineTotal } from "../utils/productPricing";
import {
  PENDING_ORDER_DRAFT_TTL_MS,
  type PendingOrderDraft,
  type SavePendingDraftInput,
} from "../utils/pendingOrderDraft";

const CART_STORAGE_KEY = "cart";
const PENDING_ORDER_DRAFT_KEY = "pending_order_draft";

export type { PendingOrderDraft };

interface CartStorage {
  cashId: string | null;
  outletId: string | null;
  items: CartItem[];
}

const emptyCartStorage = (): CartStorage => ({
  cashId: null,
  outletId: null,
  items: [],
});

function loadCartFromStorage(): CartStorage {
  const savedCart = localStorage.getItem(CART_STORAGE_KEY);
  if (!savedCart) {
    return emptyCartStorage();
  }

  try {
    const parsed = JSON.parse(savedCart);
    if (Array.isArray(parsed)) {
      return { cashId: null, outletId: null, items: parsed };
    }
    if (parsed && Array.isArray(parsed.items)) {
      return {
        cashId: parsed.cashId ?? null,
        outletId: parsed.outletId ?? null,
        items: parsed.items,
      };
    }
  } catch (error) {
    console.error("Error loading cart:", error);
  }

  return emptyCartStorage();
}

export const useCartLogic = () => {
  const [cartStorage, setCartStorage] = useState<CartStorage>(loadCartFromStorage);
  const items = cartStorage.items;

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartStorage));
  }, [cartStorage]);

  const [hasPendingOrderDraft, setHasPendingOrderDraft] = useState<boolean>(
    () => !!localStorage.getItem(PENDING_ORDER_DRAFT_KEY)
  );

  const [draftRevision, setDraftRevision] = useState(0);

  const bumpDraftRevision = useCallback(() => {
    setDraftRevision((n) => n + 1);
  }, []);

  const pendingOrderDraft = useMemo((): PendingOrderDraft | null => {
    if (!hasPendingOrderDraft) return null;
    try {
      const raw = localStorage.getItem(PENDING_ORDER_DRAFT_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as PendingOrderDraft;
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftRevision нужен для перечитывания черновика после bumpDraftRevision
  }, [hasPendingOrderDraft, draftRevision]);

  const bindCartScope = useCallback((cashId: string, outletId?: string | null) => {
    setCartStorage((prev) => {
      if (prev.cashId && prev.cashId !== cashId) {
        return {
          cashId,
          outletId: outletId ?? null,
          items: [],
        };
      }
      return {
        ...prev,
        cashId,
        outletId: outletId ?? null,
      };
    });
  }, []);

  const isCartScopeMismatch = useCallback(
    (cashId: string) =>
      cartStorage.cashId !== null && cartStorage.cashId !== cashId,
    [cartStorage.cashId]
  );

  const sameProductId = (a: string | number, b: string | number) =>
    String(a) === String(b);

  const addToCart = useCallback((product: ProductUI, quantity: number = 1) => {
    setCartStorage((prev) => {
      const prevItems = prev.items;
      const existingItem = prevItems.find((i) =>
        sameProductId(i.product.id, product.id)
      );
      if (existingItem) {
        return {
          ...prev,
          items: prevItems.map((i) =>
            sameProductId(i.product.id, product.id)
              ? { ...i, quantity: i.quantity + quantity }
              : i
          ),
        };
      }
      return {
        ...prev,
        items: [...prevItems, { product, quantity }],
      };
    });
  }, []);

  const setItemQuantity = useCallback(
    (product: ProductUI, quantity: number) => {
      setCartStorage((prev) => {
        const prevItems = prev.items;
        if (quantity <= 0) {
          return {
            ...prev,
            items: prevItems.filter(
              (i) => !sameProductId(i.product.id, product.id)
            ),
          };
        }

        const exists = prevItems.find((i) =>
          sameProductId(i.product.id, product.id)
        );
        if (exists) {
          return {
            ...prev,
            items: prevItems.map((i) =>
              sameProductId(i.product.id, product.id) ? { ...i, quantity } : i
            ),
          };
        }
        return {
          ...prev,
          items: [...prevItems, { product, quantity }],
        };
      });
    },
    []
  );

  const updateQuantity = useCallback(
    (productId: string, quantity: number) => {
      const product = items.find((i) =>
        sameProductId(i.product.id, productId)
      )?.product;
      if (product) setItemQuantity(product, quantity);
    },
    [items, setItemQuantity]
  );

  const removeFromCart = useCallback((productId: string) => {
    setCartStorage((prev) => ({
      ...prev,
      items: prev.items.filter((i) => !sameProductId(i.product.id, productId)),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setCartStorage((prev) => ({ ...prev, items: [] }));
  }, []);

  const savePendingOrderDraft = useCallback(
    (draft: SavePendingDraftInput) => {
      const expiresAt =
        draft.expiresAt ??
        new Date(Date.now() + PENDING_ORDER_DRAFT_TTL_MS).toISOString();
      const payload: PendingOrderDraft = {
        ...draft,
        createdAt: new Date().toISOString(),
        expiresAt,
      };
      localStorage.setItem(PENDING_ORDER_DRAFT_KEY, JSON.stringify(payload));
      setHasPendingOrderDraft(true);
      bumpDraftRevision();
    },
    [bumpDraftRevision]
  );

  const clearPendingOrderDraft = useCallback(() => {
    localStorage.removeItem(PENDING_ORDER_DRAFT_KEY);
    setHasPendingOrderDraft(false);
    bumpDraftRevision();
  }, [bumpDraftRevision]);

  const restorePendingOrderDraft = useCallback(() => {
    const rawDraft = localStorage.getItem(PENDING_ORDER_DRAFT_KEY);
    if (!rawDraft) return false;

    try {
      const draft: PendingOrderDraft = JSON.parse(rawDraft);
      if (!Array.isArray(draft.items) || draft.items.length === 0) {
        clearPendingOrderDraft();
        return false;
      }

      setCartStorage((prev) => ({
        ...prev,
        items: draft.items,
      }));
      clearPendingOrderDraft();
      return true;
    } catch (error) {
      console.error("Failed to restore pending order draft:", error);
      clearPendingOrderDraft();
      return false;
    }
  }, [clearPendingOrderDraft]);

  const getItemQuantity = useCallback(
    (productId: string) => {
      const item = items.find((i) => sameProductId(i.product.id, productId));
      return item ? item.quantity : 0;
    },
    [items]
  );

  const totalQuantity = useMemo(
    () => items.reduce((total, i) => total + i.quantity, 0),
    [items]
  );

  const totalAmount = useMemo(
    () => items.reduce((total, i) => total + getLineTotal(i.product, i.quantity), 0),
    [items]
  );

  return {
    items,
    cartCashId: cartStorage.cashId,
    cartOutletId: cartStorage.outletId,
    bindCartScope,
    isCartScopeMismatch,
    addToCart,
    setItemQuantity,
    removeFromCart,
    clearCart,
    hasPendingOrderDraft,
    pendingOrderDraft,
    savePendingOrderDraft,
    clearPendingOrderDraft,
    restorePendingOrderDraft,
    getItemQuantity,
    updateQuantity,
    totalQuantity,
    totalAmount,
  };
};
